import { useCallback, useMemo, useState } from 'react';
import { SortDirection } from '@amzn/stencil-react-components/table';
import type {
  GetRiskScoreFilterOptionsCommandInput,
  ListRiskScoresCommandInput,
} from '@amzn/gbsa-helios-typescript-client';

import { useDebounced } from 'src/v2/shared/forms/useDebounced';
import { usePersistedFilters } from 'src/v2/shared/forms/usePersistedFilters';
import {
  buildRiskScoreFilterOptionsRequest,
  buildRiskScoreRequest,
} from '../logic/riskScoreRequest';
import { riskSortToRequest } from '../logic/riskFilters';
import { decodeRiskScoreWindow } from '../logic/riskTimeWindow';
import {
  createDefaultRiskEngineFilters,
  type RiskEngineFilters,
} from '../components/RiskEngineFilterBar';

/** Rows fetched per request — one page at a time, server-side. */
export const RISK_ENGINE_PAGE_SIZE = 50;

/**
 * The faceted options query refetches as the user narrows the selection. Debouncing the selection
 * that feeds it collapses a burst of chip toggles into a single call; the list query stays
 * immediate (it refetches on every applied change, matching the existing filter behavior).
 */
const OPTIONS_DEBOUNCE_MS = 300;

/**
 * Persistence key for the five Risk Engine filters (timeWindow, locations, categories, metrics,
 * statuses). Uses the same `usePersistedFilters` framework as the audit/task lists — filters
 * survive navigation and are shareable via the URL. Sort and pagination stay ephemeral: a fresh
 * view starts at page one.
 */
const RISK_ENGINE_FILTERS_PAGE_KEY = 'risk-engine';

/** Stable default-filter reference for usePersistedFilters (consumed immutably). */
const RISK_ENGINE_FILTER_DEFAULTS = createDefaultRiskEngineFilters();

/** The table's current sort, in Stencil's shape. */
export interface RiskEngineSort {
  readonly activeSortId: string | undefined;
  readonly sortDirection: SortDirection;
}

export interface UseRiskEngineListFiltersParams {
  readonly businessUnit: string | undefined;
  readonly savedLocations: readonly string[];
}

export interface UseRiskEngineListFiltersResult {
  /** Selection with locations intersected against saved locations — for the filter bar. */
  readonly activeFilters: RiskEngineFilters;
  /** Effective locations actually queried (falls back to all saved when none selected). */
  readonly requestLocations: readonly string[];
  readonly sort: RiskEngineSort;
  readonly currentPage: number;
  readonly pageSize: number;
  /** Immediate list request for the current page, filters, and sort. */
  readonly listRequest: ListRiskScoresCommandInput;
  /** Debounced faceted options request reflecting the current cross-field selection. */
  readonly filterOptionsRequest: GetRiskScoreFilterOptionsCommandInput;
  readonly onApplyFilters: (next: RiskEngineFilters) => void;
  readonly onSort: (state: { sortId: string; sortDirection: SortDirection }) => void;
  readonly onPageChange: (page: number) => void;
}

function intersectWithSaved(
  selected: readonly string[],
  savedLocations: readonly string[],
): string[] {
  const savedSet = new Set(savedLocations);
  return selected.filter((location) => savedSet.has(location));
}

/** Falls back to every saved location when the user has selected none. */
function effectiveLocations(
  selected: readonly string[],
  savedLocations: readonly string[],
): readonly string[] {
  return selected.length > 0 ? selected : savedLocations;
}

/**
 * Owns Risk Engine list filter, sort, and page state, and derives the two requests the page needs:
 * an immediate `listRequest` and a debounced, selection-aware `filterOptionsRequest`. Applying a
 * filter or changing the sort returns to page one — a new view starts at the top.
 */
export function useRiskEngineListFilters({
  businessUnit,
  savedLocations,
}: UseRiskEngineListFiltersParams): UseRiskEngineListFiltersResult {
  const { filters: persistedFilters, replaceFilters } =
    usePersistedFilters<RiskEngineFilters>(
      RISK_ENGINE_FILTERS_PAGE_KEY,
      RISK_ENGINE_FILTER_DEFAULTS,
    );
  // A shared/tampered URL could carry an unknown `timeWindow`; normalise it to a valid window so
  // the request and the toggle never see a bogus value. Memoised on the (change-stable) persisted
  // object so the derived `filters` identity only changes when a filter is actually applied.
  const filters = useMemo<RiskEngineFilters>(
    () => ({
      ...persistedFilters,
      timeWindow: decodeRiskScoreWindow(persistedFilters.timeWindow),
    }),
    [persistedFilters],
  );
  const [sort, setSort] = useState<RiskEngineSort>({
    activeSortId: undefined,
    sortDirection: SortDirection.None,
  });
  const [currentPage, setCurrentPage] = useState(1);

  const selectedLocations = useMemo(
    () => intersectWithSaved(filters.locations, savedLocations),
    [filters.locations, savedLocations],
  );
  const requestLocations = effectiveLocations(selectedLocations, savedLocations);
  // An empty location selection means "all saved locations" — the request already scopes to them,
  // so surface that same effective set in the filter bar. Otherwise the list loads location-scoped
  // while the chips show nothing selected. Narrowing to a subset reflects that subset; clearing
  // every chip falls back to all saved again (there is no meaningful zero-location view).
  const activeFilters = useMemo<RiskEngineFilters>(
    () => ({ ...filters, locations: [...requestLocations] }),
    [filters, requestLocations],
  );

  const onApplyFilters = useCallback(
    (next: RiskEngineFilters): void => {
      replaceFilters(next);
      // A new filter set is a fresh view — back to page one.
      setCurrentPage(1);
    },
    [replaceFilters],
  );
  const onSort = useCallback(
    (state: { sortId: string; sortDirection: SortDirection }): void => {
      setSort({ activeSortId: state.sortId, sortDirection: state.sortDirection });
      setCurrentPage(1);
    },
    [],
  );
  const onPageChange = useCallback((page: number): void => {
    setCurrentPage(page);
  }, []);

  const listRequest = useMemo<ListRiskScoresCommandInput>(
    () =>
      buildRiskScoreRequest({
        businessUnitId: businessUnit,
        locations: requestLocations,
        timeWindow: filters.timeWindow,
        maxResults: RISK_ENGINE_PAGE_SIZE,
        offset: (currentPage - 1) * RISK_ENGINE_PAGE_SIZE,
        categories: filters.categories,
        metrics: filters.metrics,
        statuses: filters.statuses,
        ...riskSortToRequest(sort.activeSortId, sort.sortDirection),
      }),
    [businessUnit, filters, requestLocations, currentPage, sort],
  );

  // `filters` is referentially stable between renders (it changes only on apply), so debouncing it
  // by reference settles cleanly OPTIONS_DEBOUNCE_MS after the last applied change.
  const debouncedFilters = useDebounced(filters, OPTIONS_DEBOUNCE_MS);
  const debouncedRequestLocations = useMemo(
    () =>
      effectiveLocations(
        intersectWithSaved(debouncedFilters.locations, savedLocations),
        savedLocations,
      ),
    [debouncedFilters.locations, savedLocations],
  );
  const filterOptionsRequest = useMemo<GetRiskScoreFilterOptionsCommandInput>(
    () =>
      buildRiskScoreFilterOptionsRequest({
        businessUnitId: businessUnit,
        locations: debouncedRequestLocations,
        timeWindow: debouncedFilters.timeWindow,
        categories: debouncedFilters.categories,
        metrics: debouncedFilters.metrics,
        statuses: debouncedFilters.statuses,
      }),
    [businessUnit, debouncedFilters, debouncedRequestLocations],
  );

  return {
    activeFilters,
    requestLocations,
    sort,
    currentPage,
    pageSize: RISK_ENGINE_PAGE_SIZE,
    listRequest,
    filterOptionsRequest,
    onApplyFilters,
    onSort,
    onPageChange,
  };
}
